/**
 * Groups roles by their role_group, filters out roles with null role_group,
 * and ensures that the 'Other' group comes last in the array.
 * 
 * Transforms the role objects to have `label` and `value` keys.
 * 
 * @param {Array<Object>} roles - The array of role objects to be grouped.
 * @param {number} roles.id - The unique identifier for the role.
 * @param {string} roles.role_type - The type of the role.
 * @param {string|null} roles.role_group - The group to which the role belongs.
 * @returns {Array<Object>} An array of objects where each object contains a role_group and its associated roles.
 */
export const groupRolesByRoleGroup = (roles) => {
    // Group roles by role_group
    const groupedRoles = roles.reduce((acc, role) => {
        if (role.role_group === null) return acc;  // Filter out null role_group
  
        const group = role.role_group;
        if (!acc[group]) {
            acc[group] = {
                order: role.role_group_display_order,
                roles: []
            };
        }
        acc[group].roles.push({
            label: role.role_type || role.label,
            value: role.id || role.value
        });
        return acc;
    }, {});

    // Convert groupedRoles object to an array for sorting
    const groupedRolesArray = Object.keys(groupedRoles).map(key => ({
        group: key,
        order: groupedRoles[key].order,
        roles: groupedRoles[key].roles
    }));

    // Sort the array based on role_group_display_order
    groupedRolesArray.sort((a, b) => a.order - b.order);

    // Remove the order property from the final output
    const sortedGroupedRoles = groupedRolesArray.map(group => ({
        group: group.group,
        roles: group.roles
    }));

    return sortedGroupedRoles;
};
